import React from 'react'

import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import {
  RichTextDisplay,
  RichTextEditor,
  resultType,
} from '@factorialco/f0-react/dist/experimental'

import TextArea from 'modules/core/components/TextArea'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import { AnswerQuestion } from 'modules/questionnaires/components/Answer/AnswerQuestion'
import {
  TextAnswer as AnswerType,
  Question as QuestionType,
} from 'modules/questionnaires/lib/types'

type Props = {
  question: QuestionType
  answer?: AnswerType | null
  position?: number
  placeholder?: string
  readonly?: boolean
  onChange: (answer: AnswerType) => void
  hasAttemptedSubmit?: boolean
}

export const AnswerText: React.FC<Props> = ({
  question,
  answer,
  position,
  placeholder,
  readonly,
  onChange,
  hasAttemptedSubmit = false,
}) => {
  const { editorConfig, canUseRichTextEditor, loading: loadingRichTextEditor } = useRichTextEditor()

  const [value, setValue] = React.useState<string>(answer?.answerText ?? '')
  const [open, toggle] = useToggle(false)

  const handleOnChange = (newValue: string) => {
    setValue(newValue)
    onChange({ ...answer, uuid: question.uuid, answerText: newValue })
  }

  const handleOnChangeRichText = (data: resultType) => {
    const newValue = data?.value ?? ''
    setValue(newValue)
    onChange({ ...answer, uuid: question.uuid, answerText: newValue })
  }

  const isMandatoryAndEmpty = question?.mandatory && !value

  return (
    <AnswerQuestion
      uuid={question.uuid}
      position={position}
      title={question.title}
      description={question.description}
    >
      {!loadingRichTextEditor &&
        (canUseRichTextEditor ? (
          readonly ? (
            <Box
              border={{
                all: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              }}
              borderRadius={{ all: 'abs016' }}
              padding="s16"
              background="grey000White"
            >
              <RichTextDisplay
                content={
                  value?.length > 0
                    ? value
                    : i18n.t('questionnaires.questionnaire_response.no_response')
                }
              />
            </Box>
          ) : (
            <Box position="relative">
              <Box
                border={
                  hasAttemptedSubmit && isMandatoryAndEmpty
                    ? {
                        all: {
                          color: 'radicalRed1000',
                          width: 's2',
                          style: 'solid',
                        },
                      }
                    : {}
                }
                borderRadius={hasAttemptedSubmit && isMandatoryAndEmpty ? { all: 'abs016' } : {}}
                padding={hasAttemptedSubmit && isMandatoryAndEmpty ? 's0' : 's2'}
              >
                <RichTextEditor
                  {...editorConfig}
                  title={question.title}
                  onChange={handleOnChangeRichText}
                  placeholder={placeholder ?? ''}
                  initialEditorState={{
                    content: value ?? '',
                  }}
                  maxCharacters={5000}
                />
              </Box>

              {hasAttemptedSubmit && isMandatoryAndEmpty && (
                <Text.Normal size="200" color="radicalRed1000" className="mt-1">
                  {i18n.t('performance_process.reviews.answer_evaluation.missing_mandatory_field')}
                </Text.Normal>
              )}

              {isMandatoryAndEmpty && (
                <Box padding="s8" position="absolute" style={{ top: '-13px', right: '-13px' }}>
                  <Tooltip.Alt
                    attachment="bottom center"
                    open={open}
                    toggle={toggle}
                    label={
                      hasAttemptedSubmit && isMandatoryAndEmpty
                        ? i18n.t(
                            'performance_process.reviews.answer_evaluation.missing_mandatory_field'
                          )
                        : i18n.t('common.missing_field')
                    }
                  >
                    <Box
                      aria-label={
                        hasAttemptedSubmit && isMandatoryAndEmpty
                          ? i18n.t(
                              'performance_process.reviews.answer_evaluation.missing_mandatory_field'
                            )
                          : i18n.t('common.missing_field')
                      }
                    >
                      <Icon.Small
                        icon="alert"
                        encircled={
                          hasAttemptedSubmit && isMandatoryAndEmpty ? 'radicalRed1000' : 'grey800'
                        }
                      />
                    </Box>
                  </Tooltip.Alt>
                </Box>
              )}
            </Box>
          )
        ) : (
          <TextArea
            hideLabel
            label={question.title}
            mandatory={question.mandatory}
            placeholder={placeholder}
            value={value}
            onChange={handleOnChange}
            readonly={readonly}
            error={
              hasAttemptedSubmit && isMandatoryAndEmpty
                ? i18n.t('performance_process.reviews.answer_evaluation.missing_mandatory_field')
                : undefined
            }
          />
        ))}
    </AnswerQuestion>
  )
}
